import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminHeader from '../../components/admin/AdminHeader';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { adminAPI, categoriesAPI } from '../../lib/api';
import { getEventImageUrl, DEFAULT_EVENT_IMAGE } from '../../utils/mediaUrl';
import { isStaffRole } from '../../utils/roles';
import { getEventShortAbsoluteUrl } from '../../utils/eventFormat';
import { generateBrandedQrDataUrl, QR_SIZE_DOWNLOAD } from '../../utils/brandedQr';

const EventsPage = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalEvents: 0
  });
  const [categories, setCategories] = useState([]);

  const notify = (options) => {
    Swal.fire({
      confirmButtonColor: '#ea580c',
      ...options
    });
  };

  const getShortUrl = (eventId) => getEventShortAbsoluteUrl(eventId);

  const copyEventLink = async (event) => {
    const url = getShortUrl(event.id);
    try {
      await navigator.clipboard.writeText(url);
      notify({
        icon: 'success',
        title: 'Link copied',
        text: url,
        timer: 2200,
        showConfirmButton: false
      });
    } catch {
      notify({
        icon: 'info',
        title: 'Copy this link',
        text: url
      });
    }
  };

  const downloadEventQr = async (event) => {
    const url = getShortUrl(event.id);
    try {
      const dataUrl = await generateBrandedQrDataUrl(url, {
        size: QR_SIZE_DOWNLOAD
      });
      const slug = String(event.title || 'event')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'event';
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${slug}-qr.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('QR download failed:', err);
      notify({
        icon: 'error',
        title: 'QR download failed',
        text: 'Could not generate the QR code. Try again.'
      });
    }
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await categoriesAPI.getCategories();
      setCategories(data.data?.categories || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please log in.');
        notify({
          icon: 'error',
          title: 'Authentication Required',
          text: 'Please log in to access this page.'
        });
        navigate(`/login?returnTo=${encodeURIComponent('/admin/events')}`);
        return;
      }

      const data = await adminAPI.getEvents({
        page: pagination.currentPage,
        limit: 20,
        includeDisabled: 'true',
        ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(categoryFilter !== 'all' && { category: categoryFilter })
      });

      setEvents(data.data?.events || []);
      setPagination(data.data?.pagination || { currentPage: 1, totalPages: 1, totalEvents: 0 });
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch events. Please try again.';
      setError(errorMessage);
      console.error('Error fetching events:', err);

      if (err.status === 401 || err.status === 403) {
        localStorage.removeItem('token');
        notify({
          icon: 'error',
          title: 'Access Denied',
          text: errorMessage
        });
        navigate(`/login?returnTo=${encodeURIComponent('/admin/events')}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, statusFilter, categoryFilter, pagination.currentPage]);

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    if (!isStaffRole(currentUser?.role)) {
      notify({
        icon: 'warning',
        title: 'Insufficient permissions',
        text: 'You do not have permission to disable this event.'
      });
      return;
    }

    try {
      setActionLoading(true);
      const data = await adminAPI.deleteEvent(selectedEvent.id);
      notify({
        icon: 'success',
        title: 'Event disabled',
        text: data.message || 'The event has been disabled successfully.'
      });
      fetchEvents();
      setShowDeleteModal(false);
      setSelectedEvent(null);
    } catch (err) {
      notify({
        icon: 'error',
        title: 'Failed to disable event',
        text: err.message || 'An error occurred while disabling the event.'
      });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteModal = (event) => {
    setSelectedEvent(event);
    setShowRestoreModal(false);
    setShowDeleteModal(true);
  };

  const handleRestoreEvent = async () => {
    if (!selectedEvent) return;

    if (!isStaffRole(currentUser?.role)) {
      notify({
        icon: 'warning',
        title: 'Insufficient permissions',
        text: 'You do not have permission to restore this event.'
      });
      return;
    }

    try {
      setActionLoading(true);
      const data = await adminAPI.restoreEvent(selectedEvent.id, { status: 'ACTIVE' });
      notify({
        icon: 'success',
        title: 'Event restored',
        text: data.message || 'The event is visible to customers again.'
      });
      fetchEvents();
      setShowRestoreModal(false);
      setSelectedEvent(null);
    } catch (err) {
      notify({
        icon: 'error',
        title: 'Failed to restore event',
        text: err.message || 'An error occurred while restoring the event.'
      });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const openRestoreModal = (event) => {
    setSelectedEvent(event);
    setShowDeleteModal(false);
    setShowRestoreModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return 'Free';
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      ACTIVE: 'bg-green-100 text-green-800',
      SOLD_OUT: 'bg-red-100 text-red-800',
      CANCELLED: 'bg-gray-100 text-gray-800',
      COMPLETED: 'bg-blue-100 text-blue-800',
      DISABLED: 'bg-slate-200 text-slate-800 border border-slate-300'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const stats = {
    total: pagination.totalEvents,
    active: events.filter(e => e.status === 'ACTIVE').length,
    soldOut: events.filter(e => e.status === 'SOLD_OUT').length,
    cancelled: events.filter(e => e.status === 'CANCELLED' && !e.deletedAt).length,
    disabled: events.filter(e => e.deletedAt).length
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      <AdminSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col ml-0 lg:ml-72">
        <AdminHeader onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="flex-1 p-6 lg:p-8 w-full overflow-y-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                  <i className="fas fa-calendar-alt mr-3 text-orange-600"></i>
                  Event Management
                </h1>
                <p className="text-gray-600">Manage all platform events, bookings, and availability</p>
              </div>
              <Link
                to="/admin/events/create"
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium flex items-center"
              >
                <i className="fas fa-plus mr-2"></i>
                Create Event
              </Link>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Events</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-md flex items-center justify-center">
                  <i className="fas fa-calendar text-orange-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Active Events</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.active}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-md flex items-center justify-center">
                  <i className="fas fa-check-circle text-green-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Sold Out</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.soldOut}</p>
                </div>
                <div className="w-12 h-12 bg-red-100 rounded-md flex items-center justify-center">
                  <i className="fas fa-ticket-alt text-red-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-gray-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Cancelled</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.cancelled}</p>
                </div>
                <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
                  <i className="fas fa-times-circle text-gray-600 text-xl"></i>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-slate-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Disabled</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.disabled}</p>
                </div>
                <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center">
                  <i className="fas fa-ban text-slate-600 text-xl"></i>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <i className="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search events by title, description, or venue..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex space-x-4">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="ACTIVE">Active</option>
                  <option value="SOLD_OUT">Sold Out</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="COMPLETED">Completed</option>
                </select>
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => fetchEvents()}
                  className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium"
                >
                  <i className="fas fa-sync-alt mr-2"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Events Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
                <p className="text-gray-600">Loading events...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <i className="fas fa-exclamation-circle text-red-500 text-4xl mb-4"></i>
                <p className="text-red-600 font-medium">Error: {error}</p>
                <button
                  onClick={fetchEvents}
                  className="mt-4 px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  Try Again
                </button>
              </div>
            ) : events.length === 0 ? (
              <div className="p-12 text-center">
                <i className="fas fa-calendar-times text-gray-400 text-4xl mb-4"></i>
                <p className="text-gray-600 mb-4">No events found</p>
                <Link to="/admin/events/create" className="inline-block px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700">
                  Create Event
                </Link>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Event
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Date & Venue
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Tickets
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {events.map((event) => (
                        <tr key={event.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                <img
                                  src={getEventImageUrl(event.imageUrl)}
                                  alt={event.title}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = DEFAULT_EVENT_IMAGE;
                                  }}
                                />
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900">{event.title}</p>
                                <p className="text-sm text-gray-500">
                                  {event.categories?.length
                                    ? event.categories.map((c) => c.name).join(', ')
                                    : event.category?.name}
                                </p>
                                <p className="text-sm text-gray-500">{event.organizer?.name}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-600">
                              <div className="flex items-center mb-1">
                                <i className="fas fa-calendar mr-2 text-orange-500"></i>
                                {formatDate(event.date)}
                              </div>
                              <div className="flex items-center">
                                <i className="fas fa-map-marker-alt mr-2 text-red-500"></i>
                                {event.venue?.name || 'N/A'}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">
                              <div className="font-medium">{event._count?.tickets || 0} / {event.maxTickets}</div>
                              <div className="text-xs text-gray-500">
                                {Math.round(((event._count?.tickets || 0) / event.maxTickets) * 100)}% sold
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 text-xs font-semibold rounded-md ${getStatusBadge(event.deletedAt ? 'DISABLED' : event.status)}`}>
                              {event.deletedAt ? 'DISABLED' : event.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            {isStaffRole(currentUser?.role) ? (
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={() => {
                                    setSelectedEvent(event);
                                    setShowEventModal(true);
                                  }}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="View Details"
                                >
                                  <i className="fas fa-eye"></i>
                                </button>
                                <Link
                                  to={`/admin/sales?eventId=${event.id}`}
                                  className="p-2 text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
                                  title="Sales"
                                >
                                  <i className="fas fa-ticket-alt"></i>
                                </Link>
                                <Link
                                  to={`/admin/events/${event.id}/gate-staff`}
                                  className="p-2 text-sky-700 hover:bg-sky-50 rounded-lg transition-colors"
                                  title="Gate staff"
                                >
                                  <i className="fas fa-id-badge"></i>
                                </Link>
                                <Link
                                  to={`/events/${event.id}`}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="View on Site"
                                >
                                  <i className="fas fa-external-link-alt"></i>
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => copyEventLink(event)}
                                  className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                                  title="Copy short link"
                                >
                                  <i className="fas fa-link"></i>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => downloadEventQr(event)}
                                  className="p-2 text-violet-600 hover:bg-violet-50 rounded-lg transition-colors"
                                  title="Download QR code"
                                >
                                  <i className="fas fa-qrcode"></i>
                                </button>
                                <Link
                                  to={`/admin/events/${event.id}/edit`}
                                  className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                                  title="Edit"
                                >
                                  <i className="fas fa-edit"></i>
                                </Link>
                                {event.deletedAt ? (
                                  <button
                                    onClick={() => openRestoreModal(event)}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Restore"
                                  >
                                    <i className="fas fa-undo"></i>
                                  </button>
                                ) : (
                                  <button
                                    onClick={() => openDeleteModal(event)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Disable"
                                  >
                                    <i className="fas fa-ban"></i>
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">View only</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="bg-white px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      Showing {(pagination.currentPage - 1) * 20 + 1} to {Math.min(pagination.currentPage * 20, pagination.totalEvents)} of {pagination.totalEvents} events
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                        disabled={pagination.currentPage === 1}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 text-sm text-gray-700">
                        Page {pagination.currentPage} of {pagination.totalPages}
                      </span>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* View Event Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Event Details</h3>
                <button
                  onClick={() => {
                    setShowEventModal(false);
                    setSelectedEvent(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="flex items-start space-x-6 mb-6">
                <div className="flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden bg-gray-100">
                  <img
                    src={getEventImageUrl(selectedEvent.imageUrl)}
                    alt={selectedEvent.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = DEFAULT_EVENT_IMAGE;
                    }}
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.title}</h4>
                  <div className="flex items-center space-x-4 mb-2">
                    <span className={`px-3 py-1 text-xs font-semibold rounded-md ${getStatusBadge(selectedEvent.deletedAt ? 'DISABLED' : selectedEvent.status)}`}>
                      {selectedEvent.deletedAt ? 'DISABLED' : selectedEvent.status}
                    </span>
                    <span className="text-gray-600">{selectedEvent.category?.name}</span>
                  </div>
                  <p className="text-gray-600">{selectedEvent.organizer?.name}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Date & Time</p>
                  <p className="font-medium text-gray-900">
                    <i className="fas fa-calendar mr-2 text-orange-500"></i>
                    {formatDate(selectedEvent.date)}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{selectedEvent.startTime || 'TBA'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Venue</p>
                  <p className="font-medium text-gray-900">
                    <i className="fas fa-map-marker-alt mr-2 text-red-500"></i>
                    {selectedEvent.venue?.name || 'N/A'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{selectedEvent.venue?.city || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Tickets</p>
                  <p className="font-medium text-gray-900">
                    {selectedEvent._count?.tickets || 0} / {selectedEvent.maxTickets} sold
                  </p>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-orange-600 h-2 rounded-full"
                      style={{ width: `${Math.round(((selectedEvent._count?.tickets || 0) / selectedEvent.maxTickets) * 100)}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Price</p>
                  <p className="font-medium text-gray-900 text-2xl">
                    {formatCurrency(selectedEvent.price)}
                  </p>
                  {selectedEvent.earlyBirdPrice && (
                    <p className="text-sm text-orange-600 mt-1">Early Bird: {formatCurrency(selectedEvent.earlyBirdPrice)}</p>
                  )}
                </div>
              </div>
              <div className="mb-6">
                <h5 className="font-semibold text-gray-900 mb-2">Description</h5>
                <p className="text-gray-600">{selectedEvent.description || 'No description available'}</p>
              </div>
              <div className="border-t border-gray-200 pt-5">
                <h5 className="font-semibold text-gray-900 mb-3">Share</h5>
                <p className="text-sm text-gray-500 mb-3 break-all">{getShortUrl(selectedEvent.id)}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copyEventLink(selectedEvent)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 text-sm font-medium transition-colors"
                  >
                    <i className="fas fa-link"></i>
                    Copy short link
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadEventQr(selectedEvent)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 text-sm font-medium transition-colors"
                  >
                    <i className="fas fa-qrcode"></i>
                    Download QR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disable Confirmation Modal */}
      {showDeleteModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Disable Event</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to disable <span className="font-semibold text-gray-900">{selectedEvent.title}</span>?
              </p>
              <p className="text-sm text-red-600 mb-6">
                The event will be hidden from customers but retained in the system for historical and reporting purposes.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedEvent(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteEvent}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-50"
                >
                  {actionLoading ? 'Disabling...' : 'Disable'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Restore Event</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Do you want to restore <span className="font-semibold text-gray-900">{selectedEvent.title}</span>?
              </p>
              <p className="text-sm text-green-600 mb-6">
                The event will become visible to customers again with an active status. You can update the status after restoration if needed.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowRestoreModal(false);
                    setSelectedEvent(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRestoreEvent}
                  disabled={actionLoading}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50"
                >
                  {actionLoading ? 'Restoring...' : 'Restore'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
